import { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { CreditCard, ShoppingBag, Trash2, X } from 'lucide-react'
import { useCart } from '../context/Cart'
import { eur, imageUrl, ttc } from '../lib/format'
import type { CartItem, PricedLine } from '../lib/types'
import { Summary } from './CartSummary'

export const lineDetails = (l: PricedLine) => [l.option, l.flocage && 'Flocage club', l.club && `Boutique ${l.club}`].filter(Boolean).join(' · ')

/** Associe chaque ligne chiffrée par l'API à la ligne du panier (même ordre). */
export function useCartLines(): [CartItem, PricedLine][] {
  const { items, totals } = useCart()
  return totals.lines.map((l) => [items[l.index], l]).filter(([it]) => it) as [CartItem, PricedLine][]
}

export function Qty({ item }: { item: CartItem }) {
  const { setQty } = useCart()
  return (
    <div className="inline-flex items-center border border-slate-200 rounded-lg">
      <button type="button" onClick={() => setQty(item.key, item.qty - 1)} className="w-7 h-7 text-sm font-bold text-navy-900 hover:bg-slate-100 rounded-l-lg" aria-label="Diminuer" data-testid={`qty-minus-${item.id}`}>−</button>
      <span className="w-8 text-center text-xs font-bold" data-testid={`qty-value-${item.id}`}>{item.qty}</span>
      <button type="button" onClick={() => setQty(item.key, item.qty + 1)} className="w-7 h-7 text-sm font-bold text-navy-900 hover:bg-slate-100 rounded-r-lg" aria-label="Augmenter" data-testid={`qty-plus-${item.id}`}>+</button>
    </div>
  )
}

export default function CartDrawer() {
  const { open, setOpen, totals, setQty } = useCart()
  const lines = useCartLines()
  const location = useLocation()
  useEffect(() => { setOpen(false) }, [location, setOpen])
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('keydown', esc)
    return () => document.removeEventListener('keydown', esc)
  }, [setOpen])
  const pct = Math.min(100, Math.round((totals.subtotal_ht / 150) * 100))
  return (
    <div className={`fixed inset-0 z-50 overflow-hidden no-print ${open ? '' : 'pointer-events-none'}`} aria-hidden={!open} inert={!open}>
      <div onClick={() => setOpen(false)} className={`absolute inset-0 bg-navy-950/70 backdrop-blur-sm transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0'}`} />
      <div className="fixed inset-y-0 right-0 max-w-full flex pl-10">
        <aside className={`w-screen max-w-md bg-white shadow-2xl transition-transform duration-300 ease-in-out flex flex-col ${open ? '' : 'translate-x-full'}`} role="dialog" aria-label="Panier" data-testid="cart-drawer">
          <div className="p-5 bg-navy-900 text-white flex items-center justify-between">
            <div className="flex items-center gap-2"><ShoppingBag className="w-5 h-5 text-brand-green" /><h3 className="font-display text-lg font-bold">Mon Panier Équipement</h3></div>
            <button type="button" onClick={() => setOpen(false)} className="p-1 hover:bg-slate-800 rounded-lg" aria-label="Fermer le panier" data-testid="cart-drawer-close"><X className="w-5 h-5 text-slate-400" /></button>
          </div>
          <div className="bg-slate-100 p-3 px-5 border-b border-slate-200">
            <div className="flex justify-between text-xs font-semibold text-slate-700 mb-1">
              <span>{totals.free_shipping ? '🎉 Livraison offerte débloquée !' : `Plus que ${eur(ttc(totals.remaining_for_free_shipping))} TTC pour la livraison offerte !`}</span><span>{pct}%</span>
            </div>
            <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden"><div className="bg-brand-green h-full transition-all duration-300" style={{ width: `${pct}%` }} /></div>
          </div>
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {lines.length ? lines.map(([item, l]) => (
              <div key={item.key} className="flex gap-3 border-b border-slate-100 pb-4" data-testid={`cart-line-${l.id}`}>
                <span className="relative shrink-0">
                  <img src={imageUrl(l.image, 200)} alt="" className="w-16 h-16 rounded-lg object-cover bg-slate-100" />
                  {l.promo_rate > 0 && <span className="absolute -top-1.5 -left-1.5 bg-red-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">-{Math.round(l.promo_rate * 100)}%</span>}
                </span>
                <div className="flex-1 min-w-0">
                  <Link to={`/produit/${l.id}`} className="text-xs font-bold text-navy-900 hover:text-brand-orange line-clamp-2">{l.title}</Link>
                  <div className="text-[11px] text-slate-500">{lineDetails(l) || l.brand}</div>
                  {l.volume_rate > 0 && <div className="text-[11px] font-semibold text-brand-green">Remise volume -{Math.round(l.volume_rate * 100)}%</div>}
                  <div className="flex items-center justify-between mt-2"><Qty item={item} /><span className="text-xs font-bold text-navy-900">{eur(ttc(l.total_ht))} TTC</span></div>
                </div>
                <button type="button" onClick={() => setQty(item.key, 0)} className="self-start p-1 text-slate-400 hover:text-red-500" aria-label="Retirer" data-testid={`cart-remove-${l.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            )) : (
              <div className="text-center py-12 space-y-3" data-testid="cart-empty">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400"><ShoppingBag className="w-7 h-7" /></div>
                <p className="text-sm font-semibold text-navy-900">Votre panier est vide</p>
                <Link to="/catalogue" className="inline-flex text-xs font-bold text-brand-orange hover:underline">Parcourir le catalogue</Link>
              </div>
            )}
          </div>
          <div className="p-5 bg-slate-50 border-t border-slate-200 space-y-3 text-slate-600">
            <Summary t={totals} scope="drawer" />
            <div className="pt-2 space-y-2">
              <Link to="/commande" className={`w-full bg-brand-green hover:bg-green-600 text-white font-bold py-3 rounded-xl shadow-lg transition-colors flex items-center justify-center gap-2 text-sm ${lines.length ? '' : 'pointer-events-none opacity-50'}`} data-testid="cart-checkout-link"><CreditCard className="w-4 h-4" />Valider la commande</Link>
              <Link to="/panier" className="w-full block text-center bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold py-2 rounded-xl transition-colors text-xs" data-testid="cart-detail-link">Voir le panier détaillé</Link>
            </div>
          </div>
        </aside>
      </div>
    </div>
  )
}
